import { lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { UsersLiveTile } from './UsersLiveTile';
import { useAuthStore } from '@/app/auth/stores/auth.store';
import { Button } from '@/shared/components/Button';
import type { ModuleDefinition } from '@/shared/types/module';
import { useAdminUserStore } from './lib/admin-user.store';

const AdminUsersPage = lazy(() =>
  import('./pages/users/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })),
);

const UserManagementQuickAction = () => {
  const { t } = useTranslation('userManagement');
  const canView = useAuthStore((state) => state.isManagerOrAdmin());
  const openCreate = useAdminUserStore((s) => s.openCreate);

  if (!canView) {
    return null;
  }

  return (
    <Button
      color="sky"
      variant="soft"
      fullWidth
      leadingIcon={<Plus size={16} />}
      onClick={() => {
        openCreate();
      }}
    >
      {t('actions.newUser')}
    </Button>
  );
};

export const userManagementModule: ModuleDefinition = {
  id: 'user-management',
  name: 'User Management',
  routes: [
    {
      path: '/admin/users',
      element: <AdminUsersPage />,
      handle: { layout: 'app', roles: ['admin', 'manager'] },
    },
  ],
  navigation: [],
  dashboard: {
    actions: [{ Component: UserManagementQuickAction, order: 40 }],
    live: [{ Component: UsersLiveTile, order: 40 }],
  },
};

// Public module surface — other modules must import from here, not deep paths.
export { useAdminOrganizations, useAdminUsers } from './hooks/useAdmin';
export { useAdminUserStore } from './lib/admin-user.store';
export { AdminUserDetailsModal } from './pages/users/components/AdminUserDetailsModal';
export type { AdminUser, RoleName } from './lib/admin.schema';
